import { useAccountStore } from '@/stores/account';
import type { RouteLocationRaw, RouteRecordNameGeneric } from 'vue-router';
import type { AccessRolAction, AccessRolSection } from '../types/accessRol';
import {
  cardExternalUserExclusion,
  cardExternalUserRouteExclusion,
  hasOpenOnboarding,
  openOnboardingActionExclusion,
} from '../utils/exclusionValidation.util';
import type { AccessRouteExclusion } from '../types/exclusions.type';

/**
 * Composable para manejar exclusiones de acceso.
 * Permite centralizar las exclusiones de acceso en un solo lugar.
 * Este composable se utiliza tanto para secciones de la UI como para el ruteo de la plataforma,
 * cada una con un método particular para validar la regla de exclusión.
 *
 * @returns Objeto con métodos para manejar exclusiones de acceso
 */
export function useAccessExclusions() {
  const accountStore = useAccountStore();

  /**
   * Determina si una sección específica de la UI debe estar bloqueada por reglas de exclusión (aplicado en useAccessRol).
   * @param section La sección a validar
   * @returns true si el acceso está bloqueado por una regla de exclusión.
   */
  const isSectionExcluded = (section: AccessRolSection): boolean => {
    // REGLA: Usuario externo de tarjeta (Exclusión Total excepto CARD)
    if (cardExternalUserExclusion({ level: accountStore.getLevel, section })) {
      return true;
    }

    return false;
  };

  function isActionExcluded(
    section: AccessRolSection,
    action: AccessRolAction
  ): boolean {
    if (isSectionExcluded(section)) return true;

    return openOnboardingActionExclusion({
      isOnboardingOpen: hasOpenOnboarding(accountStore.kycStatus),
      section,
      action,
    });
  }

  /**
   * Determina si una ruta específica debe estar bloqueada por reglas de exclusión.
   * @param fromRouteName El nombre de la ruta origen.
   * @param toRouteName El nombre de la ruta destino a validar.
   * @returns { AccessRouteExclusion }
   */
  const isRouteExcluded = (
    fromRouteName: RouteRecordNameGeneric | null,
    toRouteName: RouteRecordNameGeneric
  ): AccessRouteExclusion => {
    const response = {
      isExcluded: false,
      redirectRouteName: null as RouteRecordNameGeneric | null,
    };

    // REGLA: Usuario externo de tarjeta
    const isExternalCardUser = cardExternalUserRouteExclusion({
      level: accountStore.getLevel,
      fromRouteName,
      toRouteName,
    });

    if (isExternalCardUser.isExcluded) {
      return isExternalCardUser;
    }

    return response;
  };

  /**
   * Evalúa si la ruta a la cual se intenta acceder está bloqueada por alguna regla de exclusión
   * @param fromRouteName La ruta origen.
   * @param toRouteName La ruta destino que debe evaluarse.
   * @returns La ruta a la cual redirigir, o null si no hay una regla específica
   */
  const getExcludedRedirect = (
    fromRouteName: RouteRecordNameGeneric | null,
    toRouteName: RouteRecordNameGeneric
  ): RouteLocationRaw | null => {
    const validateRouteExclusions = isRouteExcluded(fromRouteName, toRouteName);
    if (
      validateRouteExclusions.isExcluded &&
      validateRouteExclusions.redirectRouteName
    ) {
      return { name: validateRouteExclusions.redirectRouteName };
    }

    return null;
  };

  return {
    isSectionExcluded,
    isActionExcluded,
    isRouteExcluded,
    getExcludedRedirect,
  };
}
